import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider
} from '@mui/material';
import {
  Dashboard,
  People,
  TrendingUp,
  Security
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';

const DashboardPage: React.FC = () => {
  const { user, signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const stats = [
    {
      title: 'Total Candidates',
      value: '1,234',
      change: '+12%',
      icon: People,
      color: '#3f51b5'
    },
    {
      title: 'Active Jobs',
      value: '56',
      change: '+8%',
      icon: Dashboard,
      color: '#2196f3'
    },
    {
      title: 'Placement Rate',
      value: '78%',
      change: '+5%',
      icon: TrendingUp,
      color: '#4caf50'
    },
    {
      title: 'Security Score',
      value: '95%',
      change: '+2%',
      icon: Security,
      color: '#ff9800'
    }
  ];

  const recentActivities = [
    {
      id: 1,
      title: 'New candidate application',
      description: 'John Doe applied for Senior Developer position',
      time: '2 hours ago',
      avatar: 'JD'
    },
    {
      id: 2,
      title: 'Interview scheduled',
      description: 'Sarah Smith - Product Manager interview at 3 PM',
      time: '4 hours ago',
      avatar: 'SS'
    },
    {
      id: 3,
      title: 'Job posting published',
      description: 'Frontend Developer position is now live',
      time: '1 day ago',
      avatar: 'FD'
    },
    {
      id: 4,
      title: 'Candidate hired',
      description: 'Mike Johnson accepted the offer for DevOps Engineer',
      time: '2 days ago',
      avatar: 'MJ'
    }
  ];

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Welcome back, {user?.name || 'User'}!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Here's what's happening with your recruitment activities today.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={handleSignOut}
          sx={{ borderRadius: 2 }}
        >
          Sign Out
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ borderRadius: 3, height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: stat.color,
                        width: 48,
                        height: 48,
                        mr: 2
                      }}
                    >
                      <IconComponent />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight={700}>
                        {stat.value}
                      </Typography>
                      <Chip
                        label={stat.change}
                        size="small"
                        color="success"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Performance Chart */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Recruitment Performance
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your hiring metrics over the last 6 months
              </Typography>
              
              {/* Mock chart data */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Applications</Typography>
                  <Typography variant="body2" fontWeight={600}>85%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={85} sx={{ height: 8, borderRadius: 4 }} />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Interviews</Typography>
                  <Typography variant="body2" fontWeight={600}>72%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={72} sx={{ height: 8, borderRadius: 4 }} />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Offers</Typography>
                  <Typography variant="body2" fontWeight={600}>68%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={68} sx={{ height: 8, borderRadius: 4 }} />
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Hires</Typography>
                  <Typography variant="body2" fontWeight={600}>45%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={45} sx={{ height: 8, borderRadius: 4 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activities */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Recent Activities
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Latest updates from your recruitment pipeline
              </Typography>
              
              <List sx={{ p: 0 }}>
                {recentActivities.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                          <Typography variant="caption" fontWeight={600}>
                            {activity.avatar}
                          </Typography>
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600}>
                            {activity.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {activity.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {activity.time}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < recentActivities.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;